import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';

import { useModalContext } from '@dataswift/shared';
import DevelopersPage from '../../styles/templates/DeveloperPage';
import ProjectDetailsForm from './ProjectDetailsForm';
import { selectProjectById } from '../../redux/selectors/projects';
import { AppState } from '../../redux/reducers/rootReducer';
import { addApp, editApp } from '../../redux/applications/actions/AppActions';
import { useUser } from '../../helpers/customHooks';
import { ReadyForTestingModal } from '../../components/Modals';
import { getDeveloperApp } from '../../services/DevPortalService';
import { ApplicationInterface } from '../../interfaces/application';

type PropsType = {
  path?: string;
  default?: boolean;
};

type LocationStateType = {
  newApp?: boolean;
};

const ProjectDetailsPage: React.FC<PropsType> = () => {
  const dispatch = useDispatch();
  const match = useRouteMatch<{ projectId: string }>('/projects/details/:projectId');
  const history = useHistory();
  const project = useSelector((state: AppState) =>
    selectProjectById(state, match?.params.projectId as string),
  );
  const user = useUser();
  const [open, setOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const { state: locationState } = useLocation<LocationStateType>();
  const { openModal } = useModalContext();

  useEffect(() => {
    const fetchApp = async () => {
      const response = await getDeveloperApp(match?.params.projectId as string);
      if (!response.parsedBody) return;

      dispatch(addApp(response.parsedBody));
    };
    if (!project) fetchApp();
  }, [match, project, dispatch]);

  useEffect(() => {
    if (locationState?.newApp) {
      setTimeout(() => openModal({ open: true, component: ReadyForTestingModal }), 100);
    }
  }, [locationState, openModal]);

  if (!project) return null;

  const onUpdate = (app?: ApplicationInterface, refresh: boolean = false) => {
    if (!app) return;
    setOpen(true);
    dispatch(editApp(app));
    if (refresh) history.push(`/projects/details/${app.info.applicationId}`);
  };

  const handleClose = (_?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleErrorClose = (_?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') return;
    setErrorOpen(false);
  };

  const onPermissionSuccess = (id: string) => {
    const newApp: ApplicationInterface = {
      ...project,
      info: {
        ...project.info,
        hasPermissionsForm: true,
      },
    };
    dispatch(editApp(newApp));
  };

  return (
    <DevelopersPage>
      <ProjectDetailsForm
        project={project}
        user={user}
        onUpdate={onUpdate}
        onPermissionSuccess={onPermissionSuccess}
        onError={() => setErrorOpen(true)}
      />

      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success">
          App updated successfully!
        </Alert>
      </Snackbar>

      <Snackbar open={errorOpen} autoHideDuration={3000} onClose={handleErrorClose}>
        <Alert onClose={handleErrorClose} severity="error">
          Something went wrong!
        </Alert>
      </Snackbar>
    </DevelopersPage>
  );
};

export default ProjectDetailsPage;
